/**
 * LoginModal - Modal login form for pricing page
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/global/auth/AuthContext";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(email, password);
      // Login successful - redirect to app choice page
      onClose();
      navigate("/app-choice");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-9999 overflow-y-auto"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-md bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="relative pb-2">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 rounded-full hover:bg-white/10 text-white/80 hover:text-white"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>

          <div className="text-center pt-4">
            <CardTitle className="text-2xl font-semibold text-white mb-2">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-gray-300">
              Sign in to access your account
            </CardDescription>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6 px-8">
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 text-red-300 text-sm p-3 rounded-xl backdrop-blur-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label
                htmlFor="login-email"
                className="text-white/90 font-medium"
              >
                Email
              </Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={loading}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="login-password"
                className="text-white/90 font-medium"
              >
                Password
              </Label>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
              />
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-6 px-8 pb-8 pt-4">
            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium rounded-xl shadow-lg shadow-orange-500/25 transition-all duration-200 disabled:opacity-50 disabled:shadow-none border-0 mt-2"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>

            <p className="text-sm text-center text-gray-300">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate("/fma/pricing");
                }}
                className="text-orange-400 hover:text-orange-300 transition-colors duration-200 font-medium"
              >
                Sign up for free
              </button>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
